import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TableViewIcon from '@mui/icons-material/TableView';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import enGB from 'date-fns/locale/en-GB';
import { DataGrid, FilterSelect, DocumentChip, Badge, Tabs } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery, reportDownload } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getDocumentsReport, downloadReport } from 'src/api/admin/reports';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { startOfDay, endOfDay } from 'date-fns';

interface SearchParams {
	page?: number;
	dateFrom?: string;
	dateTo?: string;
	ownerType?: string;
	status?: string;
	type?: string;
}

export const Route = createFileRoute('/_admin/reports/documents')({
	component: DocumentsReportPage,
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		page: search.page as number | undefined,
		dateFrom: search.dateFrom as string | undefined,
		dateTo: search.dateTo as string | undefined,
		ownerType: search.ownerType as string | undefined,
		status: search.status as string | undefined,
		type: search.type as string | undefined,
	}),
});

function DocumentsReportPage() {
	const { user: authenticatedUser } = useAuthenticatedUser();
	const navigate = useNavigate();
	const search = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();
	const [menuAnchor, setMenuAnchor] = useState<(EventTarget & HTMLButtonElement) | null>(null);

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasPermission = verifyPermission([PERMISSIONS.adminReportsView]);

	useBreadcrumbLevel(0, 'Reports');
	useBreadcrumbLevel(1, 'Documents');

	// Parse URL parameters
	const ownerTypeParam = search.ownerType || 'user';
	const statusParam = search.status || '';
	const typeParam = search.type || '';
	const dateFromParam = search.dateFrom;
	const dateToParam = search.dateTo;

	const dateFrom = useMemo(() => (dateFromParam ? new Date(dateFromParam) : new Date()), [dateFromParam]);
	const dateTo = useMemo(() => (dateToParam ? new Date(dateToParam) : new Date()), [dateToParam]);

	// Map ownerType to tab index: user=0, group=1, event=2
	const getTabIndex = (type: string) => {
		if (type === 'group') return 1;
		if (type === 'event') return 2;
		return 0; // default to user
	};

	const getOwnerTypeFromTab = (tabIndex: number) => {
		if (tabIndex === 1) return 'group';
		if (tabIndex === 2) return 'event';
		return 'user';
	};

	const [activeTab, setActiveTab] = useState(getTabIndex(ownerTypeParam));

	// Sync activeTab with URL param when it changes
	useEffect(() => {
		setActiveTab(getTabIndex(ownerTypeParam));
	}, [ownerTypeParam]);

	// Fetch documents report - use local timezone for better UX
	const { data, isLoading, refetch } = useQuery({
		queryKey: ['reports-documents', dateFromParam, dateToParam, ownerTypeParam, statusParam, typeParam, search.page],
		queryFn: () =>
			getDocumentsReport({
				dateFrom: startOfDay(dateFrom).toISOString(),
				dateTo: endOfDay(dateTo).toISOString(),
				ownerType: ownerTypeParam,
				status: statusParam || undefined,
				type: typeParam || undefined,
				page: search.page,
			}),
		enabled: hasPermission,
	});

	// Fetch document types for each owner type
	const { data: userDocumentTypes } = useQuery({
		queryKey: ['document-types', 'users'],
		queryFn: () => getDocumentTypes('users'),
		enabled: hasPermission,
	});

	const { data: groupDocumentTypes } = useQuery({
		queryKey: ['document-types', 'groups'],
		queryFn: () => getDocumentTypes('groups'),
		enabled: hasPermission,
	});

	const { data: eventDocumentTypes } = useQuery({
		queryKey: ['document-types', 'events'],
		queryFn: () => getDocumentTypes('events'),
		enabled: hasPermission,
	});

	// Get document types for current ownerType
	const currentDocumentTypes = useMemo(() => {
		if (ownerTypeParam === 'user') {
			return userDocumentTypes?.data || [];
		} else if (ownerTypeParam === 'group') {
			return groupDocumentTypes?.data || [];
		} else if (ownerTypeParam === 'event') {
			return eventDocumentTypes?.data || [];
		}
		return [];
	}, [ownerTypeParam, userDocumentTypes, groupDocumentTypes, eventDocumentTypes]);

	const downloadCSVReport = useCallback(async () => {
		const query = constructQuery({
			dateFrom: startOfDay(dateFrom).toISOString(),
			dateTo: endOfDay(dateTo).toISOString(),
			ownerType: ownerTypeParam,
			status: statusParam || undefined,
			type: typeParam || undefined,
			pipe: 'true',
		});
		const blob = await downloadReport('documents', query);
		if (blob) {
			reportDownload(blob);
		}
	}, [dateFrom, dateTo, ownerTypeParam, statusParam, typeParam]);

	const handleUserClick = useCallback((event: React.MouseEvent, ownerId: string) => {
		event.stopPropagation();
		if (ownerId) {
			window.open(`/users/${ownerId}`, '_blank');
		}
	}, []);

	const handleGroupClick = useCallback((event: React.MouseEvent, ownerId: string) => {
		event.stopPropagation();
		if (ownerId) {
			window.open(`/groups/${ownerId}/users`, '_blank');
		}
	}, []);

	const handleEventClick = useCallback((event: React.MouseEvent, ownerId: string) => {
		event.stopPropagation();
		if (ownerId) {
			window.open(`/events/${ownerId}`, '_blank');
		}
	}, []);

	const handleProjectClick = useCallback((event: React.MouseEvent, projectId: string) => {
		event.stopPropagation();
		if (projectId) {
			window.open(`/projects/${projectId}`, '_blank');
		}
	}, []);

	const handleCompanyClick = useCallback(
		(event: React.MouseEvent, companyName: string, row: Record<string, unknown>) => {
			event.stopPropagation();
			// If ownerType is group, the company is the group itself
			if (row.ownerType === 'group' && row.ownerId) {
				window.open(`/groups/${row.ownerId}/users`, '_blank');
			} else if (row.ownerType === 'user') {
				// For users, find group ID from companyGroups
				const companyGroups = row.companyGroups as Array<{ id: string; name: string }> | undefined;
				if (companyGroups && companyGroups.length > 0) {
					const matchingGroup = companyGroups.find((g) => g.name === companyName);
					if (matchingGroup) {
						window.open(`/groups/${matchingGroup.id}/users`, '_blank');
					}
				}
			}
		},
		[],
	);

	const mappedColumns = useMemo(() => {
		if (!data?.columns) return [];

		// Filter columns based on ownerType - for events, include company and project
		const filteredColumns =
			ownerTypeParam === 'event' ? data.columns : data.columns.filter((c) => c.id !== 'company' && c.id !== 'project');

		return filteredColumns.map((c) => {
			const baseColumn = {
				field: c.id,
				headerName: c.label,
				flex: 1,
				sortable: false,
			};

			// Add custom renderCell for status
			if (c.id === 'status') {
				return {
					...baseColumn,
					renderCell: (params: { value: unknown }) => (
						<DocumentChip
							status={
								params.value as
									| 'accepted'
									| 'inReview'
									| 'pending'
									| 'declined'
									| 'expired'
									| 'expiryWarning'
									| 'superseded'
							}
						/>
					),
				};
			}

			// Add custom renderCell for owner name (clickable pill)
			if (c.id === 'ownerName') {
				return {
					...baseColumn,
					renderCell: (params: { value: unknown; row: Record<string, unknown> }) => {
						if (!params.value || !params.row.ownerType || !params.row.ownerId) return null;
						const ownerType = params.row.ownerType as string;
						const ownerId = params.row.ownerId as string;
						const handleClick = (e?: React.MouseEvent) => {
							e?.stopPropagation();
							if (ownerType === 'user') {
								handleUserClick(e as React.MouseEvent, ownerId);
							} else if (ownerType === 'group') {
								handleGroupClick(e as React.MouseEvent, ownerId);
							} else if (ownerType === 'event') {
								handleEventClick(e as React.MouseEvent, ownerId);
							}
						};
						return (
							<Badge variant="outline" onClick={handleClick} className="hover:cursor-pointer">
								{String(params.value)}
							</Badge>
						);
					},
				};
			}

			// Add custom renderCell for company
			if (c.id === 'company') {
				return {
					...baseColumn,
					renderCell: (params: { value: unknown; row: Record<string, unknown> }) => {
						if (!params.value) return null;
						// Company can be multiple companies separated by '/'
						const companies = String(params.value).split('/').filter(Boolean);
						return (
							<div className="flex gap-1 flex-wrap">
								{companies.map((company, index) => (
									<Badge
										key={index}
										variant="outline"
										onClick={(e?: React.MouseEvent) => handleCompanyClick(e as React.MouseEvent, company, params.row)}
										className="hover:cursor-pointer"
									>
										{company}
									</Badge>
								))}
							</div>
						);
					},
				};
			}

			// Add custom renderCell for project
			if (c.id === 'project') {
				return {
					...baseColumn,
					renderCell: (params: { value: unknown; row: Record<string, unknown> }) => {
						if (!params.value || !params.row.projectId) return null;
						return (
							<Badge
								variant="outline"
								onClick={(e?: React.MouseEvent) =>
									handleProjectClick(e as React.MouseEvent, params.row.projectId as string)
								}
								className="hover:cursor-pointer"
							>
								{String(params.value)}
							</Badge>
						);
					},
				};
			}

			return baseColumn;
		});
	}, [
		data?.columns,
		ownerTypeParam,
		handleUserClick,
		handleGroupClick,
		handleEventClick,
		handleCompanyClick,
		handleProjectClick,
	]);

	// Redirect if no permissions
	useEffect(() => {
		if (!hasPermission) {
			navigate({
				to: '/403',
				search: { permissions: 'adminReportsView', occurredAt: '/reports/documents', method: 'GET', uuid: undefined },
			});
		}
	}, [hasPermission, navigate]);

	if (!hasPermission) {
		return null;
	}

	const revalidateDocuments = () => {
		queryClient.invalidateQueries({ queryKey: ['reports-documents'] });
		refetch();
	};

	const handleTabChange = (tabIndex: number) => {
		setActiveTab(tabIndex);
		const newOwnerType = getOwnerTypeFromTab(tabIndex);
		// Reset page to 0 when switching tabs to avoid "page X of Y" issues
		updateQueryParams({ ownerType: newOwnerType, page: 0, type: '' });
	};

	const handleDateFromChange = (date: Date | null) => {
		if (date) {
			updateQueryParams({ dateFrom: startOfDay(date).toISOString(), page: 0 });
		}
	};

	const handleDateToChange = (date: Date | null) => {
		if (date) {
			updateQueryParams({ dateTo: endOfDay(date).toISOString(), page: 0 });
		}
	};

	const handleStatusFilterChange = (status: string) => {
		updateQueryParams({ status: status || undefined, page: 0 });
	};

	const handleTypeFilterChange = (type: string) => {
		updateQueryParams({ type: type || undefined, page: 0 });
	};

	const handlePaginationModelChange = ({ page }: { page: number; pageSize: number }) => {
		updateQueryParams({ page });
	};

	const handleRowClick = ({ row }: { id: string; row: Record<string, unknown> | undefined }) => {
		if (!row) return;
		// Use _id if id is not available (fallback for MongoDB _id)
		const docId = row.id || row._id;
		// Normalize ownerType to lowercase (it might be capitalized for display)
		const ownerType = ((row.ownerType as string) || '').toLowerCase();
		const ownerId = row.ownerId;

		if (!docId || !ownerType || !ownerId) {
			console.error('Missing row data:', { row });
			return;
		}

		// Construct URL based on ownerType and open in new tab
		let url = '';
		if (ownerType === 'user') {
			url = `/users/${ownerId}/documents/${docId}`;
		} else if (ownerType === 'group') {
			url = `/groups/${ownerId}/documents/${docId}`;
		} else if (ownerType === 'event') {
			url = `/events/${ownerId}/documents/${docId}`;
		}

		if (url) {
			window.open(url, '_blank');
		}
	};

	return (
		<>
			<div className="w-full p-4 pb-1">
				<div className="flex flex-row flex-wrap gap-2 w-full justify-between">
					<div className="flex flex-row gap-2">
						<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
							<DatePicker
								value={dateFrom}
								onChange={handleDateFromChange}
								maxDate={dateTo}
								slotProps={{
									textField: {
										size: 'medium',
									},
								}}
							/>
							<DatePicker
								value={dateTo}
								onChange={handleDateToChange}
								minDate={dateFrom}
								slotProps={{
									textField: {
										size: 'medium',
									},
								}}
							/>
						</LocalizationProvider>
						<FilterSelect
							listSubHeader="Filter Status"
							options={[
								{ label: 'All', value: '' },
								{ label: 'In Review', value: 'inReview' },
								{ label: 'Accepted', value: 'accepted' },
							]}
							value={statusParam}
							onChange={handleStatusFilterChange}
							size="medium"
							sx={{ width: 150 }}
						/>
						<FilterSelect
							listSubHeader="Filter Document Type"
							options={[
								{ label: 'All', value: '' },
								...currentDocumentTypes.map((dt) => ({
									label: dt.name,
									value: dt.id,
								})),
							]}
							value={typeParam}
							onChange={handleTypeFilterChange}
							size="medium"
							sx={{ width: 200 }}
						/>
					</div>
					<Button onClick={(e) => setMenuAnchor(e.currentTarget)}>
						<MoreVertIcon />
					</Button>

					<Menu
						anchorEl={menuAnchor}
						open={!!menuAnchor}
						onClose={() => setMenuAnchor(null)}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'right',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						elevation={1}
					>
						<MenuItem
							key={'Export to Excel'}
							onClick={() => {
								downloadCSVReport();
								setMenuAnchor(null);
							}}
						>
							<ListItemIcon>
								<TableViewIcon />
							</ListItemIcon>
							<ListItemText>Export to Excel</ListItemText>
						</MenuItem>
					</Menu>
				</div>
				<Tabs value={activeTab} onChange={handleTabChange} className="mt-4 -mx-4 px-4">
					<Tabs.TabList>
						<Tabs.Tab value={0} label="Users" />
						<Tabs.Tab value={1} label="Companies" />
						<Tabs.Tab value={2} label="Events" />
					</Tabs.TabList>
				</Tabs>
			</div>
			<div className="p-4 pt-0">
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						data={data?.data || []}
						columns={mappedColumns}
						rowCount={data?.count || 0}
						paginationMode="server"
						sortingMode="client"
						sortingOrder={['asc', 'desc']}
						onPaginationModelChange={handlePaginationModelChange}
						pageSize={data?.pageSize || 50}
						page={data?.page || 0}
						pagination
						isLoading={isLoading}
						onReload={revalidateDocuments}
						onRowClick={handleRowClick}
					/>
				</Paper>
			</div>
		</>
	);
}
